import { useState, useEffect, useCallback } from 'react';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { mockItems } from '../lib/mockData';
import type { Item, ItemFilters } from '../types';

export function useItems(filters?: ItemFilters) {
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      if (!isSupabaseConfigured) {
        // Use mock data
        let filtered = [...mockItems];

        if (filters?.search) {
          const search = filters.search.toLowerCase();
          filtered = filtered.filter(
            (item) =>
              item.name.toLowerCase().includes(search) ||
              item.category.toLowerCase().includes(search) ||
              item.tags.some((tag) => tag.toLowerCase().includes(search))
          );
        }

        if (filters?.category) {
          filtered = filtered.filter((item) =>
            item.category.toLowerCase().includes(filters.category.toLowerCase())
          );
        }

        if (filters?.minPrice !== null && filters?.minPrice !== undefined) {
          filtered = filtered.filter(
            (item) => item.low_price !== null && item.low_price >= filters.minPrice!
          );
        }

        if (filters?.maxPrice !== null && filters?.maxPrice !== undefined) {
          filtered = filtered.filter(
            (item) => item.high_price !== null && item.high_price <= filters.maxPrice!
          );
        }

        setItems(filtered);
        return;
      }

      let query = supabase.from('items').select('*');

      if (filters?.search) {
        query = query.ilike('name', `%${filters.search}%`);
      }

      if (filters?.category) {
        query = query.ilike('category', `%${filters.category}%`);
      }

      if (filters?.minPrice !== null && filters?.minPrice !== undefined) {
        query = query.gte('low_price', filters.minPrice);
      }

      if (filters?.maxPrice !== null && filters?.maxPrice !== undefined) {
        query = query.lte('high_price', filters.maxPrice);
      }

      const { data, error: fetchError } = await query.order('name');

      if (fetchError) throw fetchError;
      setItems(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch items');
      // Fallback to mock data on error
      setItems(mockItems);
    } finally {
      setLoading(false);
    }
  }, [filters?.search, filters?.category, filters?.minPrice, filters?.maxPrice]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  return { items, loading, error, refetch: fetchItems };
}

export function useItem(id: number | string) {
  const [item, setItem] = useState<Item | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchItem() {
      setLoading(true);
      setError(null);

      try {
        if (!isSupabaseConfigured) {
          const found = mockItems.find((i) => i.id === Number(id));
          setItem(found || null);
          return;
        }

        const { data, error: fetchError } = await supabase
          .from('items')
          .select('*')
          .eq('id', id)
          .single();

        if (fetchError) throw fetchError;
        setItem(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch item');
        // Fallback to mock data
        const found = mockItems.find((i) => i.id === Number(id));
        setItem(found || null);
      } finally {
        setLoading(false);
      }
    }

    if (id) {
      fetchItem();
    }
  }, [id]);

  return { item, loading, error };
}
